import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, map, tap, switchMap, mergeMap, filter } from 'rxjs/operators';
import { ErrorUtils } from '@shared/utils';
import { exceptionManagementFeature, selectRouteParams, ToastActions } from '@store';
import { ExceptionManagementService } from '@shared/services';
import { ExceptionManagementActions } from './exception-management.actions';

export const loadExceptions$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), exceptionManagementService = inject(ExceptionManagementService)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.loadExceptions),
      concatLatestFrom(() => [
        store.select(exceptionManagementFeature.selectExceptionsActiveFiltersRequest),
        store.select(exceptionManagementFeature.selectExceptionsAssigneeFilter),
      ]),
      mergeMap(([payload, activeFilters, assigneeFilter]) => {
        return exceptionManagementService
          .getExeptions({
            requestParams: { ...payload.params, activeFilters },
            assignedTo: assigneeFilter?.map((item) => item.value) ?? [],
          })
          .pipe(
            map((exceptionListResponse) => {
              return ExceptionManagementActions.loadExceptionsSuccess({
                exceptionListItems: exceptionListResponse.items,
                totalElements: exceptionListResponse.totalElements,
              });
            }),
            catchError((error) => {
              return of(ExceptionManagementActions.loadExceptionsFailure({ error }));
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const loadExceptionsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.loadExceptionsFailure),
      tap({
        next: ({ error }) => {
          ErrorUtils.catchError('exceptionManagementService.getExceptions - Error', error);
        },
      }),
      switchMap(({ error }) => {
        if (`${error}` === '403') {
          return of();
        }
        return of(ToastActions.error({ message: 'Unable to fetch Exceptions.' }));
      }),
    );
  },
  { functional: true },
);

export const loadExceptionDetails$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), exceptionManagementService = inject(ExceptionManagementService)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.loadExceptionDetails),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      mergeMap(([, routeParams]) => {
        return exceptionManagementService
          .getExeptionDetails({
            exceptionId: routeParams.exceptionId,
          })
          .pipe(
            map((exceptionDetails) => {
              return ExceptionManagementActions.loadExceptionDetailsSuccess({ exceptionDetails });
            }),
            catchError((error) => {
              return of(ExceptionManagementActions.loadExceptionDetailsFailure({ error }));
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const loadExceptionDetailsSuccess$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.loadExceptionDetailsSuccess),
      switchMap(({ exceptionDetails }) =>
        of(ExceptionManagementActions.loadExceptionHistory({ params: { page: 0 }, businessAccountId: exceptionDetails.businessAccountId })),
      ),
    );
  },
  { functional: true },
);

export const loadExceptionDetailsFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.loadExceptionDetailsFailure),
      tap({
        next: (errorResponse) => {
          ErrorUtils.catchError('exceptionManagementService.getExceptionDetails - Error', errorResponse.error);
        },
      }),
      filter((error) => `${error}` !== `${404}`),
      switchMap(() => of(ToastActions.error({ message: 'Unable to fetch Exception Details.' }))),
    );
  },
  { functional: true },
);

export const loadExceptionDetailsFailureRedirect$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.loadExceptionDetailsFailure),
      tap({
        next: () => router.navigateByUrl('/app/exception-management/exceptions'),
      }),
    );
  },
  { dispatch: false, functional: true },
);

export const updateException$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), exceptionManagementService = inject(ExceptionManagementService)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.updateException),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      mergeMap(([payload, routeParams]) => {
        const { assignedTo, status } = payload.data;
        return exceptionManagementService
          .updateExceptionById({
            exceptionId: routeParams.exceptionId,
            data: {
              assignedTo: assignedTo === 'UNASSIGNED' ? null : assignedTo,
              status,
            },
          })
          .pipe(
            map((exceptionDetails) => {
              return ExceptionManagementActions.updateExceptionSuccess({ exceptionDetails });
            }),
            catchError((error) => {
              return of(ExceptionManagementActions.updateExceptionFailure({ error }));
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const updateExceptionSuccess$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.updateExceptionSuccess),
      map((response) => {
        return ExceptionManagementActions.loadExceptionDetailsSuccess({ exceptionDetails: response.exceptionDetails });
      }),
    );
  },
  { functional: true },
);

export const updateExceptionSuccessNotification$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.updateExceptionSuccess),
      switchMap(() => of(ToastActions.success({ message: 'Exception updated successfully.' }))),
    );
  },
  { functional: true },
);

export const updateExceptionFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.updateExceptionFailure),
      tap({
        next: (errorResponse) => {
          ErrorUtils.catchError('exceptionManagementService.updateException - Error', errorResponse.error);
        },
      }),
      filter((error) => `${error}` !== `${404}`),
      switchMap(() => of(ToastActions.error({ message: 'Unable to update Exception.' }))),
    );
  },
  { functional: true },
);

export const loadExceptionHistory$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), exceptionManagementService = inject(ExceptionManagementService)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.loadExceptionHistory),
      concatLatestFrom(() => [store.select(selectRouteParams)]),
      mergeMap(([payload, routeParams]) => {
        return exceptionManagementService
          .getExeptionHistory({
            exceptionId: routeParams.exceptionId,
            requestParams: { ...payload.params },
          })
          .pipe(
            map((exceptionHistoryList) => {
              return ExceptionManagementActions.loadExceptionHistorySuccess({
                businessAccountId: payload.businessAccountId,
                exceptionHistoryItems: exceptionHistoryList.content ?? [],
                totalElements: exceptionHistoryList.totalElements,
              });
            }),
            catchError((error) => {
              return of(ExceptionManagementActions.loadExceptionHistoryFailure({ error }));
            }),
          );
      }),
    );
  },
  { functional: true },
);

export const loadExceptionHistoryFailure$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ExceptionManagementActions.loadExceptionHistoryFailure),
      tap({
        next: (errorResponse) => {
          ErrorUtils.catchError('exceptionManagementService.getExceptionHistory - Error', errorResponse.error);
        },
      }),
      filter((error) => `${error}` !== `${404}`),
      switchMap(() => of(ToastActions.error({ message: 'Unable to fetch Exception History.' }))),
    );
  },
  { functional: true },
);
